import { apiFetch } from './client';

// Mirror of internal/api/feedback.go's ticketItem JSON shape. Adding a field
// server-side: add it here AND in the Feedback page's renderer.

export type FeedbackStatus = 'pending' | 'submitted' | 'failed' | 'open' | 'closed';

export interface FeedbackTicket {
  id: string;
  status: FeedbackStatus;
  title: string;
  body: string;
  kind?: 'bug' | 'feature_request' | 'question' | 'other' | null;
  severity?: 'low' | 'medium' | 'high' | null;
  github_issue_number?: number | null;
  github_url?: string | null;
  last_github_event?: string | null;
  failure_reason?: string | null;
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListFeedbackResponse {
  tickets: FeedbackTicket[];
  next_cursor?: string | null;
  total: number;
}

export async function listFeedback(
  token: string | null,
  slug: string,
  before?: string | null,
): Promise<ListFeedbackResponse> {
  const qs = before ? `?before=${encodeURIComponent(before)}` : '';
  return apiFetch<ListFeedbackResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback${qs}`,
    { token },
  );
}

export async function getFeedback(
  token: string | null,
  slug: string,
  ticketID: string,
): Promise<FeedbackTicket> {
  return apiFetch<FeedbackTicket>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback/${encodeURIComponent(ticketID)}`,
    { token },
  );
}

export interface SubmitFeedbackResponse {
  status: 'queued';
  ticket_id: string;
}

export type FeedbackKind = 'bug' | 'feature_request' | 'question' | 'other';

export async function submitFeedback(
  token: string | null,
  slug: string,
  body: { title: string; body: string; kind?: FeedbackKind | ''; attachments?: File[] },
): Promise<SubmitFeedbackResponse> {
  // Always multipart, even with zero attachments — keeps the wire format
  // uniform on the server (it special-cases JSON only for the legacy AI
  // command-intent path).
  const fd = new FormData();
  fd.append('title', body.title);
  fd.append('body', body.body);
  if (body.kind) {
    // Omit the field entirely when the user left it on "auto" so the
    // server INSERT keeps kind NULL and the classifier fills it.
    fd.append('kind', body.kind);
  }
  for (const f of body.attachments ?? []) {
    fd.append('attachment', f, f.name);
  }
  return apiFetch<SubmitFeedbackResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback`,
    { token, method: 'POST', body: fd },
  );
}

// Attachment client-side limits. The server enforces the same rules (and
// the validation pipeline ALSO sniffs by magic bytes), but echoing them
// here lets the dropzone reject invalid files before upload.
export const FEEDBACK_MAX_ATTACHMENTS = 3;
export const FEEDBACK_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const FEEDBACK_ACCEPTED_TYPES: ReadonlyArray<string> = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const FEEDBACK_ACCEPT_ATTR =
  '.png,.jpg,.jpeg,.gif,.webp,.pdf,.xlsx,image/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export async function withdrawFeedback(
  token: string | null,
  slug: string,
  ticketID: string,
): Promise<void> {
  await apiFetch(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback/${encodeURIComponent(ticketID)}`,
    { token, method: 'DELETE' },
  );
}

// --- Comment thread on a ticket ---
//
// Mirrors core.feedback_ticket_comment via the api/feedback_comments.go
// handler. `author_kind` distinguishes a user-typed reply ('user'), a
// maintainer reply mirrored from GitHub ('maintainer'), or a reserved
// bot-authored row ('app'). `status` follows the outbound posting flow —
// 'posting' shows a spinner, 'failed' surfaces a retry CTA, 'posted' is
// the rest state.

export type FeedbackCommentAuthor = 'user' | 'maintainer' | 'app';
export type FeedbackCommentStatus = 'posting' | 'posted' | 'failed';

export interface FeedbackComment {
  id: string;
  author_kind: FeedbackCommentAuthor;
  author_label?: string | null;
  body: string;
  status: FeedbackCommentStatus;
  github_comment_id?: number | null;
  github_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListFeedbackCommentsResponse {
  comments: FeedbackComment[];
}

export async function listFeedbackComments(
  token: string | null,
  slug: string,
  ticketID: string,
): Promise<ListFeedbackCommentsResponse> {
  return apiFetch<ListFeedbackCommentsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback/${encodeURIComponent(ticketID)}/comments`,
    { token },
  );
}

export interface PostFeedbackCommentResponse {
  status: 'queued';
  comment_id: string;
}

export async function postFeedbackComment(
  token: string | null,
  slug: string,
  ticketID: string,
  body: string,
): Promise<PostFeedbackCommentResponse> {
  return apiFetch<PostFeedbackCommentResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/feedback/${encodeURIComponent(ticketID)}/comments`,
    { token, method: 'POST', body: { body } },
  );
}

// Body length contract — mirrored in the Go handler + DB CHECK. Echoing
// the upper bound here lets the textarea validate before posting.
export const FEEDBACK_COMMENT_BODY_MAX = 8000;
